import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { organizationsApi } from '@/api/organizations.api';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { formatDateTime } from '@/lib/format';

const PLAN_PRICE: Record<string, string> = {
  free: '0 AZN / ay',
  business: '49 AZN / ay',
  enterprise: '__contact_sales__',
};

export function AdminBillingPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ['billing'],
    queryFn: () => organizationsApi.billing(),
  });

  if (isLoading || !data) {
    return (
      <AdminLayout title={t('admin.billing_title')} subtitle={t('admin.billing_subtitle')}>
        <div className="card text-center py-12 text-muted">{t('common.loading')}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={t('admin.billing_title')} subtitle={t('admin.billing_subtitle')}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        <div className="card">
          <div className="text-[11px] uppercase font-semibold text-muted tracking-wider">
            {t('admin.current_plan')}
          </div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">
            {t(`settings_billing.plan_${data.plan}`)}
          </div>
          <div className="text-[12px] text-muted mt-0.5">
            {PLAN_PRICE[data.plan] === '__contact_sales__' ? t('settings_billing.contact_sales') : PLAN_PRICE[data.plan]}
          </div>
          {data.trialEndsAt && (
            <div className="text-[12px] text-warning mt-3">
              {t('admin.trial_ends')} {formatDateTime(data.trialEndsAt)}
            </div>
          )}
        </div>

        <div className="card">
          <div className="text-[11px] uppercase font-semibold text-muted tracking-wider">
            {t('admin.billing_period')}
          </div>
          <div className="text-[13px] text-gray-900 mt-2">
            <strong>{t('admin.from')}:</strong> {formatDateTime(data.periodStart)}
          </div>
          <div className="text-[13px] text-gray-900">
            <strong>{t('admin.to')}:</strong> {formatDateTime(data.periodEnd)}
          </div>
        </div>

        <div className="card bg-primary text-white border-primary flex flex-col justify-between">
          <div>
            <div className="text-[14px] font-semibold mb-1">{t('admin.upgrade_title')}</div>
            <p className="text-[12px] text-white/80 m-0">{t('admin.upgrade_text')}</p>
          </div>
          <button
            disabled
            className="btn mt-3"
            style={{ background: '#fff', color: '#0F2A47', border: 0, opacity: 0.7, cursor: 'not-allowed' }}
          >
            {t('admin.upgrade_btn')}
          </button>
        </div>
      </div>

      <div className="card !p-0 mb-5">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="text-base font-semibold text-gray-900 m-0">{t('admin.usage')}</h3>
        </div>
        <div className="p-4 space-y-4">
          <UsageBar label={t('admin.usage_envelopes')} used={data.envelopes.used} limit={data.envelopes.limit} />
          <UsageBar label={t('admin.usage_users')} used={data.users.used} limit={data.users.limit} />
          <div className="flex justify-between text-[13px] text-gray-700">
            <span>{t('admin.usage_bulk')}</span>
            <strong>{data.bulkSend.limit > 0 ? data.bulkSend.limit : t('admin.unlimited')}</strong>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function UsageBar({ label, used, limit }: { label: string; used: number; limit: number }) {
  const unlimited = limit <= 0;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const overage = !unlimited && used >= limit;
  return (
    <div>
      <div className="flex justify-between text-[12px] mb-1.5">
        <span className="text-gray-700">{label}</span>
        <span className={overage ? 'text-danger font-semibold' : 'text-gray-900'}>
          <strong>{used.toLocaleString()}</strong>
          {unlimited ? ' (∞)' : ` / ${limit.toLocaleString()}`}
        </span>
      </div>
      {!unlimited && (
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${pct}%`,
              background: overage ? '#EF4444' : pct > 80 ? '#F59E0B' : '#0F2A47',
            }}
          />
        </div>
      )}
    </div>
  );
}
